import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ImageCarouselProps {
  images: string[];
  className?: string;
  showArrows?: boolean;
  showDots?: boolean;
  onImageClick?: (index: number) => void;
  fullscreen?: boolean;
  activeIndex?: number;
  onIndexChange?: (index: number) => void;
}

export function ImageCarousel({
  images,
  className = '',
  showArrows = true,
  showDots = true,
  onImageClick,
  fullscreen = false,
  activeIndex: controlledIndex,
  onIndexChange,
}: ImageCarouselProps) {
  const [internalIndex, setInternalIndex] = useState(0);
  const currentIndex = controlledIndex !== undefined ? controlledIndex : internalIndex;
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const [touchDeltaX, setTouchDeltaX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const setCurrentIndex = (index: number) => {
    if (onIndexChange) {
      onIndexChange(index);
    } else {
      setInternalIndex(index);
    }
  };

  const goToNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;
    touchEndX.current = e.touches[0].clientX;
    const deltaX = touchEndX.current - touchStartX.current;
    
    // Prevent vertical swipe when horizontal swiping
    if (Math.abs(deltaX) > 10) {
      e.stopPropagation();
    }
    
    // Add resistance at edges
    if ((deltaX > 0 && currentIndex === 0) || (deltaX < 0 && currentIndex === images.length - 1)) {
      setTouchDeltaX(deltaX * 0.3);
    } else {
      setTouchDeltaX(deltaX);
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    
    const swipeDistance = touchEndX.current - touchStartX.current;
    const threshold = 50;

    if (swipeDistance < -threshold && currentIndex < images.length - 1) {
      goToNext();
    } else if (swipeDistance > threshold && currentIndex > 0) {
      goToPrevious();
    }

    setIsSwiping(false);
    setTouchDeltaX(0);
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Reset index when images change
  useEffect(() => {
    if (currentIndex >= images.length) {
      setCurrentIndex(Math.max(0, images.length - 1));
    }
  }, [images.length]);

  if (images.length === 0) return null;

  if (images.length === 1) {
    return (
      <div 
        className={`relative overflow-hidden flex items-center justify-center ${className}`}
        onClick={() => onImageClick?.(0)}
      >
        <img
          src={images[0]}
          alt="Post image"
          className="w-full h-full object-contain"
          loading="eager"
        />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Images container */}
      <div 
        className="flex transition-transform duration-300 ease-out h-full"
        style={{
          transform: `translateX(calc(-${currentIndex * 100}% + ${touchDeltaX}px))`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
        }}
      >
        {images.map((image, index) => (
          <div
            key={index}
            className="w-full flex-shrink-0 h-full flex items-center justify-center"
            style={{ minWidth: '100%' }}
            onClick={() => onImageClick?.(index)}
          >
            <img
              src={image}
              alt={`Post image ${index + 1}`}
              className="max-w-full max-h-full object-contain"
              style={{ 
                width: 'auto',
                height: 'auto',
                maxHeight: '100%',
                maxWidth: '100%',
              }}
              loading={index === currentIndex || index === currentIndex + 1 || index === currentIndex - 1 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>

      {/* Navigation arrows */}
      {showArrows && (
        <>
          {currentIndex > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          )}
          {currentIndex < images.length - 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          )}
        </>
      )}

      {/* Dots indicator */}
      {showDots && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                goToIndex(index);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentIndex
                  ? 'bg-white w-4'
                  : 'bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Image counter badge */}
      <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
        {currentIndex + 1}/{images.length}
      </div>
    </div>
  );
}
